/**
 * Keep cart attributes in sync before checkout.
 * - Re-fetch the latest cart footer section to avoid stale "発送予定" values.
 * - Update attributes before normal and dynamic checkout flows.
 *
 * 先方検証用: チェックアウト直前の同期時のみ、注文メモ（cart note）に識別行を1行追加する。
 * プレフィックス [STG-RENEWAL] で管理画面の注文メモを検索可能。
 */
const WL_CHECKOUT_DEBUG_NOTE_PREFIX = '[STG-RENEWAL] checkout-sync';
const WL_CHECKOUT_DEBUG_THEME_ID = 'stg-renewal';

class DynamicCheckoutFix {
  constructor() {
    this.isSyncing = false;
    this.shouldBypassCartSubmitInterception = false;
    this.latestAdditionalAttributes = null;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.onReady());
      return;
    }
    this.onReady();
  }

  onReady() {
    this.setupCartCheckoutInterception();
    this.setupDynamicCheckoutInterception();
    this.proactiveInitialSync();
  }

  isCartPage() {
    return window.location.pathname.indexOf('/cart') === 0;
  }

  async proactiveInitialSync() {
    this.toggleDynamicCheckoutButtons(false);
    const fallbackTimer = window.setTimeout(() => this.toggleDynamicCheckoutButtons(true), 3000);

    try {
      await this.syncAttributesFromLatestCartState({ includeCheckoutDebugNote: false });
    } catch (_error) {
      // keep checkout available via fallback timer
    } finally {
      window.clearTimeout(fallbackTimer);
      this.toggleDynamicCheckoutButtons(true);
    }
  }

  setupCartCheckoutInterception() {
    const cartForm = document.getElementById('cart');
    if (!cartForm) return;

    cartForm.addEventListener(
      'submit',
      async (event) => {
        if (this.shouldBypassCartSubmitInterception) {
          this.shouldBypassCartSubmitInterception = false;
          return;
        }

        event.preventDefault();
        const submitter = event.submitter || null;

        try {
          await this.syncAttributesFromLatestCartState({ includeCheckoutDebugNote: true });
        } catch (_error) {
          this.showCartError('カートの更新に失敗しました。処理を継続します。');
        } finally {
          this.resumeCartSubmit(cartForm, submitter);
        }
      },
      true
    );
  }

  resumeCartSubmit(cartForm, submitter) {
    this.shouldBypassCartSubmitInterception = true;

    // requestSubmit keeps submitter context, so name="checkout" is preserved.
    if (submitter && typeof cartForm.requestSubmit === 'function') {
      cartForm.requestSubmit(submitter);
      return;
    }

    // Fallback for browsers without submitter/requestSubmit support.
    if (submitter && submitter.name) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = submitter.name;
      hidden.value = submitter.value || '';
      hidden.dataset.dynamicCheckoutFallback = 'true';
      cartForm.appendChild(hidden);
    }

    cartForm.submit();

    const fallbackInput = cartForm.querySelector('input[data-dynamic-checkout-fallback="true"]');
    if (fallbackInput) fallbackInput.remove();
  }

  setupDynamicCheckoutInterception() {
    document.addEventListener(
      'click',
      async (event) => {
        const button = event.target.closest(
          '.additional-checkout-buttons button, .additional-checkout-buttons [role="button"], .shopify-payment-button button, shop-pay-wallet-button, shopify-apple-pay-button, shopify-google-pay-button, shopify-paypal-button'
        );

        if (!button || button.dataset.dynamicCheckoutSyncBypass === 'true') return;
        if (this.isSyncing) return;

        event.preventDefault();
        event.stopPropagation();

        this.toggleDynamicCheckoutButtons(false);

        try {
          await this.syncAttributesFromLatestCartState({ includeCheckoutDebugNote: true });
        } catch (_error) {
          // do not block checkout
        } finally {
          button.dataset.dynamicCheckoutSyncBypass = 'true';
          this.toggleDynamicCheckoutButtons(true);

          window.setTimeout(() => {
            button.click();
            window.setTimeout(() => {
              delete button.dataset.dynamicCheckoutSyncBypass;
            }, 300);
          }, 0);
        }
      },
      true
    );
  }

  toggleDynamicCheckoutButtons(disabled) {
    const nodes = document.querySelectorAll(
      '.additional-checkout-buttons button, .additional-checkout-buttons [role="button"], .shopify-payment-button button'
    );
    nodes.forEach((node) => {
      if ('disabled' in node) {
        node.disabled = disabled;
      }
      node.setAttribute('aria-disabled', String(disabled));
      node.style.pointerEvents = disabled ? 'none' : '';
      node.style.opacity = disabled ? '0.6' : '';
    });
  }

  /**
   * @param {{ includeCheckoutDebugNote?: boolean }} [options]
   */
  async syncAttributesFromLatestCartState(options) {
    if (this.isSyncing) return;
    this.isSyncing = true;
    const includeCheckoutDebugNote = Boolean(options && options.includeCheckoutDebugNote);

    try {
      if (this.isCartPage()) {
        await this.refreshAdditionalAttributesFromServer();
      }
      const debugNote = includeCheckoutDebugNote ? await this.buildCheckoutDebugNoteForUpdate() : null;
      await this.updateCartAttributes(debugNote);
    } finally {
      this.isSyncing = false;
    }
  }

  async refreshAdditionalAttributesFromServer() {
    const response = await fetch(`/cart?_wl_sync_ts=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Failed to fetch latest cart page');

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const latestAttributes = {};
    const latestInputs = doc.querySelectorAll(
      'input[data-additional-attribute], select[data-additional-attribute], textarea[data-additional-attribute]'
    );
    latestInputs.forEach((input) => {
      const name = input.getAttribute('data-additional-attribute');
      if (!name) return;
      latestAttributes[name] = input.value || '';
    });

    if (Object.keys(latestAttributes).length === 0) {
      this.latestAdditionalAttributes = null;
      throw new Error('No additional attributes found in latest cart HTML');
    }

    this.latestAdditionalAttributes = latestAttributes;

    // Keep visible cart UI consistent with the latest fetched value.
    const latestShipping = latestAttributes['発送予定'];
    const currentInput = document.querySelector('input[data-additional-attribute="発送予定"]');
    if (currentInput && typeof latestShipping !== 'undefined') {
      currentInput.value = latestShipping;
      currentInput.setAttribute('value', latestShipping);
    }
  }

  /**
   * 最新カートの note に識別行を差し込む（同一プレフィックスの旧行は除去して上書き相当）。
   * cart.js 取得失敗時は null（メモ更新をスキップし、属性更新のみ継続）。
   */
  async buildCheckoutDebugNoteForUpdate() {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) return null;
      const cart = await res.json();
      const existing = (cart.note || '').trim();
      const shipping =
        (this.latestAdditionalAttributes && this.latestAdditionalAttributes['発送予定']) ||
        (() => {
          const el = document.querySelector('input[data-additional-attribute="発送予定"]');
          return el ? el.value : '';
        })();
      const line = `${WL_CHECKOUT_DEBUG_NOTE_PREFIX} theme=${WL_CHECKOUT_DEBUG_THEME_ID} | items=${cart.item_count} | ts=${new Date().toISOString()} | 発送予定同期値=${shipping}`;
      const keptLines = existing
        .split('\n')
        .filter((l) => !l.trim().startsWith(WL_CHECKOUT_DEBUG_NOTE_PREFIX));
      const base = keptLines.join('\n').trim();
      return base ? `${base}\n${line}` : line;
    } catch (_error) {
      return null;
    }
  }

  /**
   * @param {string | null} [debugNote] チェックアウト直前のみ渡す（注文メモ = cart note）
   */
  async updateCartAttributes(debugNote) {
    const attributes = this.collectCartAttributes();
    const payload = {};
    if (Object.keys(attributes).length > 0) {
      payload.attributes = attributes;
    }
    if (debugNote) {
      payload.note = debugNote;
    }
    if (!payload.attributes && typeof payload.note === 'undefined') {
      return;
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Cart update failed: ${response.status}`);
    }
  }

  collectCartAttributes() {
    const attributes = {};
    const inputs = document.querySelectorAll(
      'input[data-additional-attribute], select[data-additional-attribute], textarea[data-additional-attribute]'
    );

    inputs.forEach((input) => {
      const name = input.getAttribute('data-additional-attribute');
      if (!name) return;
      attributes[name] = input.value || '';
    });

    // Prioritize latest server-fetched values to avoid stale DOM values.
    if (this.latestAdditionalAttributes) {
      Object.assign(attributes, this.latestAdditionalAttributes);
    }

    return attributes;
  }

  showCartError(message) {
    const errorDiv = document.getElementById('cart-errors');
    if (!errorDiv) return;
    errorDiv.textContent = message;
    errorDiv.classList.add('form__message--error');
  }
}

if (typeof window !== 'undefined') {
  window.dynamicCheckoutFix = new DynamicCheckoutFix();
}
